import { useState } from 'react';
import { api } from '../api';
import { toast } from './Toast';
import BarcodeScanner from './BarcodeScanner';

export default function ProductModal({ product, drawers, onClose, onSaved }) {
  const isEdit = !!product?.id;
  const [form, setForm] = useState({
    name: '', barcode: '', quantity: 1, unit: 'stuks',
    expiry_date: '', drawer_id: '', position_notes: '', category: '', notes: '',
    ...product,
  });
  const [barcodeInput, setBarcodeInput] = useState('');
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [barcodeFound, setBarcodeFound] = useState(null);
  const [barcodeFromCache, setBarcodeFromCache] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const lookupBarcode = async (code) => {
    if (!code) return;
    setBarcodeLoading(true);
    setBarcodeFound(null);
    setBarcodeFromCache(false);
    try {
      const data = await api.get(`/barcode/${code}`);
      if (data.found) {
        setBarcodeFromCache(!!data.fromCache);
        // Only fill in fields that are still empty — never overwrite manual edits
        setForm(f => ({
          ...f,
          barcode: code,
          name: f.name.trim() ? f.name : (data.name || f.name),
          category: f.category ? f.category : (data.category || ''),
          unit: f.unit !== 'stuks' ? f.unit : (data.unit || f.unit),
          quantity: f.quantity !== 1 ? f.quantity : (data.default_quantity || f.quantity),
          notes: f.notes.trim() ? f.notes : (data.notes || f.notes),
        }));
        setBarcodeFound(data.name || 'Product gevonden');
        toast(data.fromCache ? 'Geladen uit jouw cache ✓' : 'Product gevonden via Open Food Facts ✓');
      } else {
        set('barcode', code);
        toast('Barcode niet gevonden — vul gegevens zelf in', 'error');
      }
    } catch {
      toast('Fout bij opzoeken barcode', 'error');
    }
    setBarcodeLoading(false);
  };

  const handleScanResult = (code) => {
    setShowScanner(false);
    setBarcodeInput(code);
    lookupBarcode(code);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast('Naam is verplicht', 'error'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        quantity: parseFloat(form.quantity) || 1,
        drawer_id: form.drawer_id || null,
        expiry_date: form.expiry_date || null,
      };

      if (isEdit) {
        await api.put(`/products/${product.id}`, payload);
      } else {
        await api.post('/products', payload);
      }

      // Save to barcode cache if product has a barcode
      if (form.barcode && form.barcode.trim()) {
        try {
          await api.put(`/barcode-cache/${form.barcode.trim()}`, {
            name: form.name.trim(),
            category: form.category || '',
            unit: form.unit || 'stuks',
            default_quantity: parseFloat(form.quantity) || 1,
            notes: form.notes || '',
          });
        } catch (e) {
          // Cache save failure is non-critical
          console.warn('Cache save failed:', e);
        }
      }

      toast(isEdit ? 'Product bijgewerkt ✓' : 'Product toegevoegd ✓');
      onSaved();
    } catch (e) {
      toast(e.message, 'error');
    }
    setSaving(false);
  };

  const units = ['stuks', 'g', 'kg', 'ml', 'l', 'portie', 'zak', 'blik', 'doos'];
  const categories = ['Vlees', 'Vis', 'Groenten', 'Fruit', 'Zuivel', 'Brood', 'Maaltijd', 'Snacks', 'Drank', 'Overig'];

  return (
    <>
      {showScanner && (
        <BarcodeScanner
          onDetected={handleScanResult}
          onClose={() => setShowScanner(false)}
        />
      )}

      <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="modal">
          <div className="modal-header">
            <h3>{isEdit ? 'Product bewerken' : 'Product toevoegen'}</h3>
            <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>✕</button>
          </div>
          <div className="modal-body">

            <div style={{ marginBottom: 16 }}>
              <label className="form-label">Barcode</label>
              <button
                className="btn btn-primary"
                style={{ width: '100%', marginBottom: 10, padding: '12px', fontSize: 15, justifyContent: 'center', gap: 10 }}
                onClick={() => setShowScanner(true)}
                type="button"
              >
                <span style={{ fontSize: 20 }}>📷</span>
                Scan met camera
              </button>
              <div className="barcode-area">
                <input
                  className="form-input barcode-input"
                  placeholder="Of typ barcode handmatig..."
                  value={barcodeInput}
                  onChange={e => setBarcodeInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && lookupBarcode(barcodeInput)}
                  style={{ flex: 1 }}
                  inputMode="numeric"
                />
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => lookupBarcode(barcodeInput)}
                  disabled={barcodeLoading || !barcodeInput}
                  type="button"
                >
                  {barcodeLoading ? <span className="loading-spinner" /> : 'Zoeken'}
                </button>
              </div>

              {barcodeFound && (
                <div className="barcode-found" style={{ background: barcodeFromCache ? 'rgba(91,196,245,0.12)' : 'var(--ok-dim)', borderColor: barcodeFromCache ? 'rgba(91,196,245,0.3)' : 'rgba(34,211,160,0.3)', color: barcodeFromCache ? 'var(--ice)' : 'var(--ok)' }}>
                  {barcodeFromCache ? '⚡' : '✓'} {barcodeFromCache ? 'Uit cache: ' : 'Gevonden: '}<strong>{barcodeFound}</strong>
                </div>
              )}

              {form.barcode && !barcodeFound && !barcodeLoading && (
                <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', marginTop: 4 }}>
                  Barcode: {form.barcode}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Naam *</label>
              <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Productnaam..." autoComplete="off" />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Hoeveelheid</label>
                <input className="form-input" type="number" min="0.1" step="0.1" value={form.quantity} onChange={e => set('quantity', e.target.value)} inputMode="decimal" />
              </div>
              <div className="form-group">
                <label className="form-label">Eenheid</label>
                <select className="form-select" value={form.unit} onChange={e => set('unit', e.target.value)}>
                  {units.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Houdbaarheidsdatum</label>
                <input className="form-input" type="date" value={form.expiry_date || ''} onChange={e => set('expiry_date', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Categorie</label>
                <select className="form-select" value={form.category} onChange={e => set('category', e.target.value)}>
                  <option value="">— Geen —</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Lade</label>
              <select className="form-select" value={form.drawer_id || ''} onChange={e => set('drawer_id', e.target.value)}>
                <option value="">— Geen lade —</option>
                {drawers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Positie</label>
              <input className="form-input" value={form.position_notes} onChange={e => set('position_notes', e.target.value)} placeholder="bv. links achteraan..." />
            </div>

            <div className="form-group">
              <label className="form-label">Notities</label>
              <textarea className="form-textarea" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Opmerkingen..." style={{ minHeight: 60 }} />
            </div>

            {form.barcode && (
              <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius2)', padding: '10px 12px', fontSize: 12, color: 'var(--text3)' }}>
                💾 Naam, categorie, eenheid en notities worden bij opslaan opgeslagen in de barcode-cache voor volgende keer.
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={onClose}>Annuleren</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? <span className="loading-spinner" /> : (isEdit ? 'Opslaan' : 'Toevoegen')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
